import { useEffect, useState } from "react";
import styles from "./TimeTracking.module.css";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import axios from 'axios';
import { toast } from 'react-smart-toaster';
import { useAuth0 } from "@auth0/auth0-react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AddLogCallAPI } from "../../../components/AddLogs";
import { startOfPayWeek, endOfPayWeek } from "../../../utils/payWeek";
import { WEEKLY_REGULAR_LIMIT, calcWeeklyPay, formatMinutesToHM, isWeekend, workdayIndex } from "./weeklyPay";

dayjs.extend(customParseFormat);

const hhmmToDecimal = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    return h + m / 60;
}

const timeToDecimal = (time) => {
    const parsed = dayjs(time, ['HH:mm:ss', 'h:mm A', 'hh:mm A', 'H:mm'], true);
    if (!parsed.isValid()) return null;
    return parsed.hour() + parsed.minute() / 60;
}

const decimalToTime = (decimal) => {
    const hours = Math.floor(decimal);
    return dayjs().hour(hours % 24).minute(0).second(0).format('h A');
}

const fmt12 = t => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM"; const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

const toP = (h, base, span) => Math.max(0, Math.min(100, ((h - base) / span) * 100));

const pct = (part, whole) => `${Math.max(0, Math.min(100, whole > 0 ? (part / whole) * 100 : 0))}%`;

const dayKind = (date) => isWeekend(date) ? 'Weekend' : `Weekday · Workday ${workdayIndex(date)} of 5`;

export const Avatar = ({ employee, size = 34 }) => (
    <div className={`${styles.av}`} style={{ width: size, height: size }}>
        {employee?.user_image_url ? <img src={employee?.user_image_url} alt="" /> : employee?.initial}
    </div>
);

// MUI clock time picker (always the mobile dialog); clicking anywhere on the
// field opens it. Reads and writes "HH:mm" strings.
const timeFieldSx = {
    '& .MuiInputBase-root': { height: 44, borderRadius: '8px', fontFamily: 'inherit', fontSize: 14, color: '#1E1B3A', cursor: 'pointer' },
    '& .MuiInputBase-input': { padding: '0 10px', cursor: 'pointer' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E7E5F0' },
    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D5D3DF' },
    '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3F2FA8', borderWidth: 2 },
    '& .MuiInputBase-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#B42318' },
};

const ClockTimeField = ({ id, label, placeholder, value, onChange, error }) => (
    <div className={`${styles.addTimeField}`}>
        <label className={`${styles.addTimeLabel}`} htmlFor={id}>{label}</label>
        <MobileTimePicker
            ampm
            value={value ? dayjs(value, 'HH:mm') : null}
            onChange={(v) => onChange(v && v.isValid() ? v.format('HH:mm') : '')}
            slotProps={{
                textField: { id, placeholder, fullWidth: true, error: !!error, sx: timeFieldSx },
                dialog: { sx: { zIndex: '10001 !important' } },
            }}
        />
        {error && <span className={`${styles.addTimeErr}`}>{error}</span>}
    </div>
);

export const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

export const EntryFormModal = ({ setIsModalOpen, selectedEmp, selectedDay, setLoading, updateTimeTracking, editData }) => {
    const { getAccessTokenSilently } = useAuth0();

    const [startTime, setStartTime] = useState("09:00");
    const [endTime, setEndTime] = useState("17:00");
    const [nextDayCheckout, setNextDayCheckout] = useState(false);

    const [lbStart, setLbStart] = useState("");
    const [lbEnd, setLbEnd] = useState("");
    const [lbNextDay, setLbNextDay] = useState(false);
    const [includeLb, setIncludeLb] = useState(true);
    const [errors, setErrors] = useState({});

    const weekend = isWeekend(selectedDay?.date);

    function validate() {
        const e = {};
        if (!startTime) e.startTime = "This field is required";
        if (!endTime) e.endTime = "This field is required";
        if (startTime && endTime && !nextDayCheckout && endTime <= startTime) {
            e.endTime = "Must be after shift start, or check \"Shift ends next day\"";
        }
        if (includeLb) {
            if (!lbStart) e.lbStart = "This field is required";
            if (!lbEnd) e.lbEnd = "This field is required";
            if (lbStart && lbEnd && !lbNextDay && lbEnd <= lbStart) {
                e.lbEnd = "Must be after break start, or check \"Break ends next day\"";
            }
        }
        return e;
    }

    useEffect(() => {
        if (editData) {
            setStartTime(editData?.checkin_time ? dayjs(editData?.checkin_time, 'HH:mm:ss').format('HH:mm') : '');
            setEndTime(editData?.checkout_time ? dayjs(editData?.checkout_time, 'HH:mm:ss').format('HH:mm') : '');
            setNextDayCheckout(!!editData?.is_overnight);

            if (editData.break_times?.lunch_start_time) {
                const breakStart = dayjs(editData?.break_times?.lunch_start_time, 'HH:mm:ss').format('HH:mm');
                const breakEnd = editData.break_times?.lunch_end_time ? dayjs(editData?.break_times?.lunch_end_time, 'HH:mm:ss').format('HH:mm') : '';
                setIncludeLb(true);
                setLbStart(breakStart);
                setLbEnd(breakEnd);
                // The list API doesn't return lunch_end_date, so a break that ends at or
                // before its start time is taken to end the next day.
                setLbNextDay(editData.break_times?.lunch_end_date
                    ? editData.break_times.lunch_end_date !== editData?.date
                    : !!breakEnd && breakEnd <= breakStart);
            } else {
                setIncludeLb(false);
            }
        } else {
            setStartTime(dayjs(selectedEmp?.checkin_time, 'hh:mm A').format('HH:mm'));
            setEndTime(dayjs(selectedEmp?.checkout_time, 'hh:mm A').format('HH:mm'));
            setNextDayCheckout(false);
            setLbNextDay(false);
        }
    }, [selectedEmp, selectedDay, editData]);

    const buildPostData = () => {
        const checkoutDate = nextDayCheckout ? dayjs(selectedDay?.date).add(1, 'day').format('YYYY-MM-DD') : selectedDay?.date;

        let postData = {
            user_id: selectedEmp?.id,
            date: selectedDay?.date,
            checkin_time: dayjs(startTime, 'HH:mm').format('HH:mm:ss'),
            checkout_time: dayjs(endTime, 'HH:mm').format('HH:mm:ss'),
            checkout_date: checkoutDate,
            is_lunch_break: includeLb ? 1 : 0
        };

        if (includeLb) {
            const lunchEndDate = lbNextDay ? dayjs(selectedDay?.date).add(1, 'day').format('YYYY-MM-DD') : selectedDay?.date;

            postData = {
                ...postData,
                lunch_start_time: dayjs(lbStart, 'HH:mm').format('HH:mm:ss'),
                lunch_end_time: dayjs(lbEnd, 'HH:mm').format('HH:mm:ss'),
                lunch_end_date: lunchEndDate
            };
        }

        return postData;
    }

    const submit = async () => {
        const e = validate();
        if (Object.keys(e).length > 0) { setErrors(e); return; }

        setLoading(true);
        setIsModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        const url = editData ? 'v1/time-tracking/update-time/' + editData?.id : 'v1/time-tracking/add-time';

        return axios.post(process.env.REACT_APP_USER_API_URL + url, buildPostData(), {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);
            updateTimeTracking(response.data.data);

            let item = response.data.data;

            let logData = {
                'title': editData ? 'Time has been update successfully.' : 'Time has been added successfully.',
                'description': [
                    'Employee name: ' + item?.user_name,
                    'Date: ' + dayjs(item?.date).format('MM/DD/YYYY'),
                ]
            }
            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const actualStart = hhmmToDecimal(startTime);
    let actualEnd = hhmmToDecimal(endTime);
    if (actualEnd !== null && actualStart !== null && nextDayCheckout) {
        actualEnd += 24;
    }

    let workedMinutes = 0;
    let breakMinutes = 0;

    if (actualStart !== null && actualEnd !== null && actualEnd > actualStart) {
        if (includeLb) {
            const lbStartDec = hhmmToDecimal(lbStart);
            let lbEndDec = hhmmToDecimal(lbEnd);
            if (lbEndDec !== null && lbStartDec !== null && lbNextDay) {
                lbEndDec += 24;
            }
            if (lbStartDec !== null && lbEndDec !== null && lbEndDec > lbStartDec) {
                breakMinutes = Math.round((lbEndDec - lbStartDec) * 60);
            }
        }
        workedMinutes = Math.max(0, Math.round((actualEnd - actualStart) * 60) - breakMinutes);
    }

    return (<div className={`${styles.modalOverlay}`} onClick={e => { if (e.target === e.currentTarget) setIsModalOpen(false); }}>
        <div className={`${styles.modal}`} role="dialog" aria-label={`${editData ? 'Update' : 'Add'} time, ${selectedEmp?.name}`}>

            <div className={`${styles.modalHead}`}>
                <div className={`${styles.mhLeft}`}>
                    <Avatar employee={selectedEmp} />
                    <div>
                        <div className={`${styles.modalName}`}>{selectedEmp?.name}</div>
                        <div className={`${styles.modalDate}`}>
                            {editData ? 'Update time' : 'Add time'} — {dayjs(selectedDay?.date).format('ddd D, MMM YYYY')} · {weekend ? <span className={`${styles.textOt}`}>Weekend</span> : 'Weekday'}
                        </div>
                    </div>
                </div>
                <button className={`${styles.modalClose}`} onClick={() => setIsModalOpen(false)} aria-label="Close"><CloseIcon /></button>
            </div>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className={`${styles.modalBody}`}>
                <div className={`${styles.addModalSection}`}>
                    <div className={`${styles.boxLbl}`}>Shift hours</div>
                    <div className={`${styles.addTimeGrid}`}>
                        <ClockTimeField id="tt-start" label="Shift start" placeholder="hh:mm am" value={startTime} error={errors.startTime} onChange={v => { setStartTime(v); setErrors(p => ({ ...p, startTime: "" })); }} />
                        <ClockTimeField id="tt-end" label="Shift end" placeholder="hh:mm pm" value={endTime} error={errors.endTime} onChange={v => { setEndTime(v); setErrors(p => ({ ...p, endTime: "" })); }} />
                    </div>
                    <label className={`${styles.checkRow}`}>
                        <input type="checkbox" checked={nextDayCheckout} onChange={e => { setNextDayCheckout(e.target.checked); setErrors(p => ({ ...p, endTime: "" })); }} />
                        Shift ends next day (overnight)
                    </label>
                </div>

                <div className={`${styles.addModalSection}`}>
                    <div className={`${styles.lbToggleRow}`}>
                        <div>
                            <div className={`${styles.boxLbl}`} style={{ marginBottom: 2 }}>Lunch break</div>
                            <div className={`${styles.lbToggleSub}`}>Did this employee take a lunch break?</div>
                        </div>
                        <label className={`${styles.checkRow}`} style={{ marginTop: 0 }}>
                            Lunch break taken
                            <input type="checkbox" checked={includeLb} onChange={e => setIncludeLb(e.target.checked)} />
                        </label>
                    </div>

                    {includeLb && (<div className={`${styles.addTimeGrid}`} style={{ marginTop: 12 }}>
                        <ClockTimeField id="tt-lbstart" label="Break start" placeholder="hh:mm pm" value={lbStart} error={errors.lbStart} onChange={v => { setLbStart(v); setErrors(p => ({ ...p, lbStart: "" })); }} />
                        <ClockTimeField id="tt-lbend" label="Break end" placeholder="hh:mm pm" value={lbEnd} error={errors.lbEnd} onChange={v => { setLbEnd(v); setErrors(p => ({ ...p, lbEnd: "" })); }} />
                    </div>)}
                    {includeLb && (<label className={`${styles.checkRow}`}>
                        <input type="checkbox" checked={lbNextDay} onChange={e => { setLbNextDay(e.target.checked); setErrors(p => ({ ...p, lbEnd: "" })); }} />
                        Break ends next day (overnight)
                    </label>)}
                </div>

                <div className={`${styles.addSummaryBox}`}>
                    <div className={`${styles.addSummaryRow}`}>
                        <span className={`${styles.addSummaryLbl}`}>Shift</span>
                        <span className={`${styles.addSummaryVal}`}>{fmt12(startTime)} – {endTime ? fmt12(endTime) : 'N/A'}{nextDayCheckout ? ' (+1d)' : ''}</span>
                    </div>
                    {includeLb && lbStart && lbEnd && (
                        <div className={`${styles.addSummaryRow}`}>
                            <span className={`${styles.addSummaryLbl}`}>Lunch break</span>
                            <span className={`${styles.addSummaryVal}`}>{fmt12(lbStart)} – {fmt12(lbEnd)}{lbNextDay ? ' (+1d)' : ''} ({formatMinutesToHM(breakMinutes)})</span>
                        </div>
                    )}
                    <div className={`${styles.addSummaryRow}`}>
                        <span className={`${styles.addSummaryLbl}`}>Regular hours</span>
                        <span className={`${styles.addSummaryVal}`}>{weekend ? '—' : formatMinutesToHM(workedMinutes)}</span>
                    </div>
                    {weekend && (<>
                        <div className={`${styles.addSummaryRow}`}>
                            <span className={`${styles.addSummaryLbl}`}>Overtime</span>
                            <span className={`${styles.addSummaryVal} ${styles.textOt}`}>{workedMinutes > 0 ? '+' + formatMinutesToHM(workedMinutes) : '—'}</span>
                        </div>
                        <div className={`${styles.addSummaryNote}`}>{dayjs(selectedDay?.date).format('dddd')} is a weekend day — all time counts as OT.</div>
                    </>)}
                </div>
            </div>
            </LocalizationProvider>

            <div className={`${styles.modalFooter}`}>
                <div className={`${styles.footerSpacer}`} />
                <button className={`${styles.btnSecondary}`} onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button className={`${styles.btnPrimary}`} onClick={() => submit()}>{editData ? 'Update time entry' : 'Add time entry'}</button>
            </div>

        </div>
    </div>)
}

export const EntryDetailModal = ({ setIsModalOpen, selectedEmp, selectedDay, deleteTimeTracking, setIsNewModalOpen, setEditData }) => {
    const [confirmDelete, setConfirmDelete] = useState(false);

    const weekend = isWeekend(selectedDay?.date);
    const pay = calcWeeklyPay(selectedEmp);
    const day = pay.days[selectedDay?.date] || { worked: 0, reg: 0, ot: 0, wtdBefore: 0, wtdAfter: 0 };
    const weekStart = startOfPayWeek(selectedDay?.date);
    const weekEnd = endOfPayWeek(selectedDay?.date);
    const weekLbl = `${weekStart.format('ddd MMM D')} – ${weekEnd.format('ddd MMM D')}`.toUpperCase();

    // ── Timeline ──
    const checkinDay = timeToDecimal(selectedDay?.checkin_time);
    const checkinEmp = weekend ? null : timeToDecimal(selectedEmp?.checkin_time);
    let checkoutEmp = weekend ? null : timeToDecimal(selectedEmp?.checkout_time);
    if (checkoutEmp !== null && checkinEmp !== null && checkoutEmp <= checkinEmp) {
        checkoutEmp += 24; // scheduled shift crosses midnight, e.g. 10PM - 5AM
    }

    let checkoutDay = timeToDecimal(selectedDay?.checkout_time);
    if (checkoutDay !== null && (selectedDay?.is_overnight || checkoutDay <= checkinDay)) {
        checkoutDay += 24;
    }
    if (checkoutDay === null && checkinDay !== null) {
        checkoutDay = checkinDay + Math.max(selectedDay?.working_time || 0, 0) / 60 + (selectedDay?.break_mintes || 0) / 60;
    }

    const barStartTime = Math.floor(Math.min(checkinDay ?? Infinity, checkinEmp ?? Infinity));
    const barEndTime = Math.ceil(Math.max(checkoutDay ?? -Infinity, checkoutEmp ?? -Infinity)) || barStartTime + 1;
    const barSpan = Math.max(1, barEndTime - barStartTime);
    const barMidTime = barStartTime + Math.round(barSpan / 2);

    const workedL = toP(checkinDay, barStartTime, barSpan);
    const workedW = toP(checkoutDay, barStartTime, barSpan) - workedL;
    const schedL = checkinEmp !== null ? toP(checkinEmp, barStartTime, barSpan) : 0;
    const schedW = checkoutEmp !== null ? toP(checkoutEmp, barStartTime, barSpan) - schedL : 0;

    // ── Week to date (weekday entries up to and including this date) ──
    const wtdParts = (selectedEmp?.timeTracking || [])
        .filter(t => !isWeekend(t?.date) && t?.date <= selectedDay?.date && pay.days[t?.date]?.worked > 0)
        .sort((a, b) => (a?.date > b?.date ? 1 : -1))
        .map(t => `${dayjs(t?.date).format('ddd')} ${formatMinutesToHM(pay.days[t?.date].worked)}`);
    const leftBeforeOt = Math.max(0, WEEKLY_REGULAR_LIMIT - day.wtdAfter);

    let countsAs = <span className={`${styles.irVal}`}>Regular · {formatMinutesToHM(day.reg)}</span>;
    if (weekend) {
        countsAs = <span className={`${styles.irVal} ${styles.textOt}`}>Overtime · {formatMinutesToHM(day.ot)}</span>;
    } else if (day.ot > 0) {
        countsAs = <span className={`${styles.countsAs}`}>
            {day.reg > 0 && <span className={`${styles.pillReg}`}>Regular · {formatMinutesToHM(day.reg)}</span>}
            <span className={`${styles.pillOt}`}>OT · {formatMinutesToHM(day.ot)}</span>
        </span>;
    }

    const schedText = weekend ? '—' : `${selectedEmp?.checkin_time || '—'} – ${selectedEmp?.checkout_time || '—'}`;

    return (<div className={`${styles.modalOverlay}`} onClick={e => { if (e.target === e.currentTarget) setIsModalOpen(false); }}>
        <div className={`${styles.modal}`} role="dialog" aria-label={`${selectedEmp?.name}, ${dayjs(selectedDay?.date).format('ddd MMM D')}`}>

            <div className={`${styles.modalHead}`}>
                <div className={`${styles.mhLeft}`}>
                    <Avatar employee={selectedEmp} />
                    <div>
                        <div className={`${styles.modalName}`}>{selectedEmp?.name}</div>
                        <div className={`${styles.modalDate}`}>{dayjs(selectedDay?.date).format('ddd D, MMM YYYY')} · {dayKind(selectedDay?.date)}</div>
                    </div>
                </div>
                <button className={`${styles.modalClose}`} onClick={() => setIsModalOpen(false)} aria-label="Close"><CloseIcon /></button>
            </div>

            <div className={`${styles.timelineWrap}`}>
                <div className={`${styles.boxLbl}`}>Shift timeline</div>
                <div className={`${styles.tlBarWrap}`}>
                    <div className={`${styles.tlTrack}`} />
                    {schedW > 0 && <div className={`${styles.tlSched}`} style={{ left: `${schedL}%`, width: `${schedW}%` }} />}
                    {workedW > 0 && <div className={`${styles.tlWorked} ${weekend ? styles.tlWorkedOt : ''}`} style={{ left: `${workedL}%`, width: `${workedW}%` }} />}
                </div>
                <div className={`${styles.tlTimes}`}>
                    <span>{decimalToTime(barStartTime)}</span><span>{decimalToTime(barMidTime)}</span><span>{decimalToTime(barEndTime)}</span>
                </div>
                <div className={`${styles.tlCiLbl}`}>
                    <span className={`${styles.tlBadge}`}>{dayjs(selectedDay?.checkin_time, 'HH:mm:ss').format('hh:mm A')}</span>
                    <span className={`${styles.tlArrow}`}>→</span>
                    {selectedDay?.checkout_time
                        ? <span className={`${styles.tlBadge}`}>{dayjs(selectedDay?.checkout_time, 'HH:mm:ss').format('hh:mm A')}{selectedDay?.is_overnight ? ' (+1d)' : ''}</span>
                        : <span className={`${styles.tlBadge} ${styles.tlBadgeProg}`}>{dayjs(selectedDay?.date).isSame(dayjs(), 'day') ? 'Now' : 'No clock-out'}</span>}
                    <span className={`${styles.tlDur}`}>{formatMinutesToHM(selectedDay?.working_time, '0m')} worked</span>
                </div>
            </div>

            <div className={`${styles.modalBody}`}>
                <div className={`${styles.infoRow}`}>
                    <span className={`${styles.irLbl}`}>Scheduled shift</span>
                    <span className={`${styles.irVal}`}>{schedText}</span>
                </div>
                <div className={`${styles.infoRow}`}>
                    <span className={`${styles.irLbl}`}>Break</span>
                    <span className={`${styles.irVal}`}>{formatMinutesToHM(selectedDay?.break_mintes, 'None')}</span>
                </div>
                <div className={`${styles.infoRow}`}>
                    <span className={`${styles.irLbl}`}>Total worked</span>
                    <span className={`${styles.irVal}`}>{formatMinutesToHM(day.worked, '0m')}</span>
                </div>
                <div className={`${styles.infoRow} ${styles.infoRowLast}`}>
                    <span className={`${styles.irLbl}`}>Counts as</span>
                    {countsAs}
                </div>

                {weekend && (<div className={`${styles.infoBox} ${styles.infoBoxOt}`}>
                    <div className={`${styles.boxLbl} ${styles.textOt}`}>Weekend shift</div>
                    <div className={`${styles.boxBig} ${styles.textOt}`}>+{formatMinutesToHM(day.ot, '0m')} overtime</div>
                    <div className={`${styles.boxText}`}>All Saturday and Sunday time counts as OT and is added to this week’s pay automatically.</div>
                </div>)}

                {!weekend && day.ot === 0 && (<div className={`${styles.infoBox}`}>
                    <div className={`${styles.boxLbl}`}>Week to date · {weekLbl}</div>
                    <div className={`${styles.boxHead}`}><span className={`${styles.boxBig}`}>{formatMinutesToHM(day.wtdAfter, '0m')}</span><span className={`${styles.boxMuted}`}>of 40h regular</span></div>
                    <div className={`${styles.bar} ${styles.barWide}`}><div className={`${styles.barFill}`} style={{ width: pct(day.wtdAfter, WEEKLY_REGULAR_LIMIT) }} /></div>
                    <div className={`${styles.boxText}`}>
                        {wtdParts.length > 0 && <>{wtdParts.join(' + ')}. </>}
                        Sat and Sun are not included (they are counted as OT). After Thu, the 5th workday, any weekday hours past 40h become OT. {formatMinutesToHM(leftBeforeOt, '0m')} left before OT.
                    </div>
                </div>)}

                {!weekend && day.ot > 0 && (<div className={`${styles.infoBox} ${styles.infoBoxOt}`}>
                    <div className={`${styles.boxLbl} ${styles.textOt}`}>Weekly OT · {weekLbl}</div>
                    <div className={`${styles.boxHead}`}><span className={`${styles.boxBig} ${styles.textOt}`}>+{formatMinutesToHM(day.ot)} overtime</span><span className={`${styles.boxMuted}`}>{formatMinutesToHM(day.wtdAfter)} of 40h</span></div>
                    <div className={`${styles.bar} ${styles.barWide} ${styles.barSplit}`}>
                        <div className={`${styles.barFill}`} style={{ width: pct(WEEKLY_REGULAR_LIMIT, day.wtdAfter) }} />
                        <div className={`${styles.barFillOt}`} style={{ width: pct(day.wtdAfter - WEEKLY_REGULAR_LIMIT, day.wtdAfter) }} />
                    </div>
                    <div className={`${styles.boxRows}`}>
                        <div className={`${styles.boxRow}`}><span>Weekday hours before today</span><span className={`${styles.boxRowVal}`}>{formatMinutesToHM(day.wtdBefore, '0m')}</span></div>
                        <div className={`${styles.boxRow}`}><span>Today (up to 40h)</span><span className={`${styles.boxRowVal}`}>+{formatMinutesToHM(day.reg, '0m')} regular</span></div>
                        <div className={`${styles.boxRow}`}><span>Today (past 40h)</span><span className={`${styles.boxRowVal} ${styles.textOt}`}>+{formatMinutesToHM(day.ot)} OT</span></div>
                    </div>
                    <div className={`${styles.boxText}`}>Weekday hours past 40h in the Fri–Thu week are counted as OT automatically.</div>
                </div>)}
            </div>

            <div className={`${styles.modalFooter}`}>
                {!confirmDelete ? (<>
                    <button className={`${styles.btnSecondary}`} onClick={() => { setIsModalOpen(false); setIsNewModalOpen(true); setEditData(selectedDay) }}>Edit</button>
                    <button className={`${styles.btnDangerOutline}`} onClick={() => setConfirmDelete(true)}>Delete</button>
                    <div className={`${styles.footerSpacer}`} />
                    <button className={`${styles.btnPrimary}`} onClick={() => setIsModalOpen(false)}>Done</button>
                </>) : (<>
                    <span className={`${styles.deleteConfirmMsg}`}>Delete this record?</span>
                    <div className={`${styles.footerSpacer}`} />
                    <button className={`${styles.btnSecondary}`} onClick={() => setConfirmDelete(false)}>Cancel</button>
                    <button className={`${styles.btnDanger}`} onClick={() => { setIsModalOpen(false); deleteTimeTracking(selectedDay?.id); }}>Yes, delete</button>
                </>)}
            </div>

        </div>
    </div>);
}
